import { Switch, Redirect, Route } from 'react-router-dom'
import PrivateRoutes from './PrivateRoutes';
import { useAuth } from '../contexts/UserContext';
import Login from '../pages/Login';
import Register from '../pages/Register';
import RegisterSolicitante from '../pages/RegisterSolicitante';
import Recover from '../pages/Login/recover';
import Layout from '../components/Layout';
import Dashboard from '../pages/Dashboard';
import Professionals from '../pages/Professionals';
import ProfessionalProfile from '../pages/ProfileProfessional';
import RegisterOperator from '../pages/RegisterOperator';
import CompleteRegisterOperator from '../pages/RegisterOperatorComplete';
import Operators from '../pages/Operators';
import OperatorProfile from '../pages/OperatorProfile';
import Proposals from '../pages/Proposals';
import NewProposal from '../pages/NewProposal';
import HearingForms from '../pages/HearingForms';
import CreateProposalComplete from '../pages/CreateProposalComplete';
import CreateUserComplete from '../pages/CreateUserComplete';
import Proposal from '../pages/ProposalEdit';
import Demandas from '../pages/Demandas';
import DemandInfo from '../pages/DemandDetails';
import Orientations from '../pages/Orientations';
import Financial from '../pages/Financial';
import WhoWeARe from '../pages/Site/WhoWeAre';
import LegalClaims from '../pages/Site/RequestLegalClaims';
import ReceiveOpportunities from '../pages/Site/ReceiveOpportunities';
import Contact from '../pages/Site/Contact';
import Cookies from '../pages/Site/CookiesPolicies';
import Home from '../pages/Site/Home';
import RequestService from '../pages/RequestService';
import CreateServiceComplete from '../pages/CreateServiceComplete';
import Notifications from "../pages/Notifications";
import Applicants from "../pages/Applicants";
import ApplicantProfile from "../pages/ApplicantProfile";
import Planos from "../pages/Planos";
import PlanosDetail from "../pages/PlanosDetail";
import ServicosTemplate from "../pages/ServicosTemplate";
import CardDemanda from '../components/CardDemanda';
import FormularioProposta from '../pages/FormularioProposta';
import Demanda from '../pages/Demanda';
import FormularioOrientacaoCompletaPage from '../pages/FormularioOrientacaoCompleta';
import McpLogin from '../pages/McpLogin';

/** HOC: protege rota por perfil. Reutilizar para novas rotas restritas (ex.: onlyAdmin, adminOuOperador). */
function withProfileGuard<P extends object>(
    Component: React.ComponentType<P>,
    options: { allowedProfiles: readonly string[]; redirectTo?: string }
): React.ComponentType<P> {
    const { allowedProfiles, redirectTo = '/demandas' } = options;
    return function ProfileGuardedRoute(props: P) {
        const { user } = useAuth();
        if (!user?.profile || !allowedProfiles.includes(user.profile)) {
            return <Redirect to={redirectTo} />;
        }
        return <Component {...props} />;
    };
}

const onlyAdmin = { allowedProfiles: ['admin'], redirectTo: '/demandas' };
/** Planos: admin (edição) e operador (somente visualização). */
const adminOrOperadorPlanos = { allowedProfiles: ['admin', 'operador'], redirectTo: '/demandas' };
const PlanosWithGuard = withProfileGuard(Planos, adminOrOperadorPlanos);
const PlanosDetailWithGuard = withProfileGuard(PlanosDetail, adminOrOperadorPlanos);
/** Template de serviços: admin (edição) e operador (somente visualização). */
const ServicosTemplateWithGuard = withProfileGuard(ServicosTemplate, adminOrOperadorPlanos);

function Routes({props}: any) {
    return (
        <Switch>
            {/* MCP OAuth2 login — rota pública, acessível independente de autenticação */}
            <Route path="/mcp/login" exact component={McpLogin} />

            {/* Free access */}
            <PrivateRoutes path="/" exact component={Home} />
            <PrivateRoutes path="/quemsomos" exact component={WhoWeARe} />
            <PrivateRoutes path="/solicitardemanda" exact component={LegalClaims} />
            <PrivateRoutes path="/receberoportunidades" exact component={ReceiveOpportunities} />
            <PrivateRoutes path="/contato" exact component={Contact} />
            <PrivateRoutes path="/politicas-cookies" exact component={Cookies} />
            <PrivateRoutes path="/login" exact component={Login} />
            <PrivateRoutes path="/cadastrar" exact component={Register} />
            <PrivateRoutes path="/cadastro-completo" exact component={CreateUserComplete} />
            <PrivateRoutes path="/solicitarservico" exact component={RequestService} />
            <PrivateRoutes path="/servico-completo" exact component={CreateServiceComplete} />
            <PrivateRoutes path="/recuperar-senha/:id" exact component={Recover} />
            <PrivateRoutes path="/registro-solicitante/:id" exact component={RegisterSolicitante} />

            {/* admin/operator access */}
            <PrivateRoutes path="/cadastrar/operador" exact component={RegisterOperator} isPrivate />
            <PrivateRoutes path="/formulario-proposta" exact component={HearingForms} isPrivate />            
            <PrivateRoutes path="/Nova-Demanda" exact component={FormularioProposta} isPrivate />            
            <PrivateRoutes path="/operador/concluir" exact component={CompleteRegisterOperator} isPrivate />            
            <PrivateRoutes path="/proposta/concluir" exact component={CreateProposalComplete} isPrivate />    
            <PrivateRoutes path="/demanda/orientacoes" exact component={Orientations} isPrivate />
            <PrivateRoutes path="/demanda/cardDemanda" exact component={CardDemanda} isPrivate  />

            <Layout {...props}>
                {/* Rotas temporariamente desativadas - redirecionam para demandas */}
                <PrivateRoutes path="/dashboard" exact component={() => <Redirect to="/demandas" />} isPrivate />
                <PrivateRoutes path="/financeiro" exact component={() => <Redirect to="/demandas" />} isPrivate />
                
                <PrivateRoutes path="/demandas" exact component={Demandas} isPrivate />
                <PrivateRoutes path="/demandas/:id/orientacoes/orientacoes-completas" exact component={FormularioOrientacaoCompletaPage} isPrivate />
                <PrivateRoutes path="/demandas/:id" exact component={Demanda} isPrivate />
                <PrivateRoutes path="/demandas/:id/:aba" exact component={Demanda} isPrivate />
                <PrivateRoutes path="/operadores" exact component={Operators} isPrivate />
                <PrivateRoutes path="/operadores/:id" exact component={OperatorProfile} isPrivate />
                <PrivateRoutes path="/nova-proposta" exact component={NewProposal} isPrivate />
                <PrivateRoutes path="/propostas" exact component={() => <Redirect to="/demandas" />} isPrivate />
                <PrivateRoutes path="/propostas/:id" exact component={Proposal} isPrivate />                
                <PrivateRoutes path="/profissionais" exact component={Professionals} isPrivate />
                <PrivateRoutes path="/profissionais/:id" exact component={ProfessionalProfile} isPrivate />
                <PrivateRoutes path="/notificacoes" exact component={Notifications} isPrivate />
                <PrivateRoutes path="/solicitantes" exact component={Applicants} isPrivate />
                <PrivateRoutes path="/solicitantes/:id" exact component={ApplicantProfile} isPrivate />
                <PrivateRoutes path="/planos" exact component={PlanosWithGuard} isPrivate />
                <PrivateRoutes path="/planos/:id" exact component={PlanosDetailWithGuard} isPrivate />
                <PrivateRoutes path="/servicos-template" exact component={ServicosTemplateWithGuard} isPrivate />
            </Layout>
        </Switch>
    )
}

export default Routes;